// ============================================================
// THE UNSTABLE PIERRE — progression.js
// Currency, exchange, and profile helpers.
//
// ── WHAT PIERRE OWNS (IE Economy Law v1, 2026-07-28) ─────────
// Pierre is the BANK OF THE EMPIRE. It owns:
//   • The Crest Well — the only exchange anywhere in IE.
//   • The rate table — one row per registered currency, live in
//     Firebase, tunable with NO deploy.
//   • grantCrests() — the single shared DC helper every title
//     imports and NOBODY reimplements.
//
// Pierre does NOT own the keystroke. Pierre isn't running while
// a player is in Terror or Snake Eyes. "Pierre owns the money"
// means Pierre owns the code, the rates, and the Well.
//
// ── WHAT DIVINE CRESTS ARE NOW ───────────────────────────────
// DC is no longer Terror's spending money. DC is the EMPIRE'S
// RESERVE, owned by no single title. Every game mints its own
// currency; all of them exchange through DC at the Well.
// DC buys ENDGAME ONLY — rarest foils, top skills, finite items.
// Never bread. Never boosters.
//
// ── RETIRED CANON — do not resurrect ─────────────────────────
//   ✗ The 5% Pierre cut. There is no exchange fee. None. The
//     Well is a service the Empire provides its Aenarchists,
//     not a business. `history.cut` stays in the schema for
//     already-written rows and is written as 0 forever.
//   ✗ `EXCHANGE_RATE = 1000` as law. It is now ONE ROW in a
//     table you own. Never hardcode a rate in a component.
//   ✗ `crestHistory` on the profile. Superseded by the
//     append-only mint log at games/pierre/mint/{uid}.
//
// Canonical references: IE_ECONOMY_LAW_v1.md (the constitution),
// PIERRE_CREST_WELL_v2_WORKORDER.md (this implementation).
// ============================================================

import { ref, get, set, update, onValue, push, runTransaction } from "firebase/database";
import { db } from "./firebase";

// ⟡ THE ENGINE FILE. Byte-identical in every IE repo — never edit one
//   copy. Pierre owns the Well; ieEconomy.js owns the registry the Well
//   reads, and every other title imports the same file to see the same
//   currencies without a deploy.
import {
  balancePath,
  currencyMeta as _currencyMeta,
  tintFor as _tintFor,
  listenToCurrency as _listenToCurrency,
  listenToWallet as _listenToWallet,
  listenToRates as _listenToRates,
  getRates as _getRates,
  grantCrests as _grantCrests,
  spendCrests as _spendCrests,
  largestMintEver as _largestMintEver,
  RATES_PATH as _RATES_PATH,
  MINT_PATH as _MINT_PATH,
  COOLDOWN_PATH as _COOLDOWN_PATH,
  HISTORY_PATH as _HISTORY_PATH,
  DC_ID as _DC_ID,
} from "./ieEconomy";
// ⭐ THE ACQUIRE-ONLY LAW. Pierre's own money is one-way at the Well: you
// may buy Dawgs with anything, and you may never sell them back. See
// upDawgs.js for why that guard is the only thing making a passive
// Minerium faucet legal under THE FAUCET LAW (L8).
// ⚠ IT LIVES UP HERE WITH THE OTHER IMPORTS ON PURPOSE. It was first
// placed beside the re-export block it relates to, which reads better
// and is an `import/first` compile ERROR in CRA. Readability lost.
import { isAcquireOnly, ACQUIRE_ONLY_REASON, UD_ID, UD_ROW } from "./upDawgs";

// ─── PROFILE ─────────────────────────────────────────────────
export const getProfileByUid = async (uid) => {
  const snap = await get(ref(db, `profiles/${uid}`));
  return snap.exists() ? { ...snap.val(), uid } : null;
};

export const listenToProfile = (uid, callback) => {
  return onValue(ref(db, `profiles/${uid}`), snap => {
    callback(snap.exists() ? { ...snap.val(), uid } : null);
  });
};

// ════════════════════════════════════════════════════════════════════
// THE CURRENCY REGISTRY  (IE Economy Law §8)
// ────────────────────────────────────────────────────────────────────
// ⚠ KEEP THIS BLOCK BYTE-IDENTICAL ACROSS EVERY IE TITLE'S
//   progression.js. Terror has a copy. Snake Eyes has a copy. New
//   titles APPEND a row here only AFTER registering a rate with Pierre.
//
// Glyphs are HUD/log fallbacks. The REAL symbol is a WebP asset on the
// CDN — see the `ie-cdn-assets` skill.
// ════════════════════════════════════════════════════════════════════

// ⟡ RECALIBRATED 2026-08-13 — THIS BLOCK NO LONGER GATES ANYTHING.
//
// It used to be a hardcoded three-row const, and `executeExchange` read
// each currency's Firebase path out of IT rather than out of the rate
// row that already carried one. So the "registration is one write, zero
// deploys" promise was only half true: you could retune a rate live,
// but a currency this const had never heard of could not appear at the
// Well AT ALL. Three shipped currencies were stranded that way —
// ⋔ Kindling, ❦ Blood Money, ❋ Dewdrops — each with a rate row nobody
// could reach.
//
// THE RATE ROW IS NOW GENUINELY THE REGISTRY. `ieEconomy.js` holds the
// registry and the resolver; this is a display fallback for a row that
// has not streamed in yet, kept under its old export name so no Pier
// call site had to change. A currency absent from here still trades.
export {
  KNOWN_CURRENCIES as CURRENCIES,
  balancePath,
  registerCurrency,
  ensureCurrency,
  perCrestFor,
  impliedHourlyFor,
} from "./ieEconomy";

// Pierre-only display tints. A row may carry its own `tint` and that
// wins; this is the fallback for the ones that don't.
export const CURRENCY_TINT = {
  DC: "#f0d060",
  CC: "#c98b4b",
  SE: "#22d3ee",
  KD: "#e0763a",
  BM: "#a83b3b",
  DW: "#7fb98a",
  SOL: "#e8b84b",
  SW: "#c9b48b",
  UD: "#9ad46a",
};

export const tintFor = (id, rates) => CURRENCY_TINT[id] || _tintFor(id, rates) || (id === UD_ID ? UD_ROW.tint : undefined);
export const currencyMeta = (id, rates) => _currencyMeta(id, rates);

// ─── PER-CURRENCY BALANCE READS ──────────────────────────────
// Any title may READ any currency. Only its owning app may write it.
// (DC is the sole exception, grant-direction only, via grantCrests.)

export const listenToCurrency = (uid, currencyId, callback, rates) =>
  _listenToCurrency(uid, currencyId, callback, rates);

// Live map of EVERY registered currency the player holds:
//   { DC: 5, CC: 600, SE: 0, KD: 40, BM: 0, DW: 30, ... }
//
// ⟡ Built from the RATE ROWS, not from a const. A title that registers
//   itself while the Pier is open appears in this wallet with no deploy
//   anywhere — which is what "the rate row is the registry" was always
//   supposed to mean. It also follows `fullPath`, so Bebecitos'
//   Dewdrops are read out of the garden and not out of a profile field
//   that does not exist.
// ✹ UP DAWGS RIDE ALONG (2026-09-16). The engine wallet is built from the
//   console rows, and UD has no console row (rates are `.write: false`),
//   so the Pier HUD never showed Dawgs at all. Pierre owns UD and adds it here.
export const listenToWallet = (uid, callback) => {
  let base = {}, ud = 0;
  const emit = () => callback({ ...base, [UD_ID]: ud });
  const a = _listenToWallet(uid, (w) => { base = w || {}; emit(); });
  const b = _listenToCurrency(uid, UD_ID, (v) => { ud = v || 0; emit(); }, { [UD_ID]: UD_ROW });
  return () => { if (typeof a === "function") a(); if (typeof b === "function") b(); };
};

// Kept for back-compat with existing Pier call sites.
export const listenToSnakeEyesCoins = (uid, callback) =>
  listenToCurrency(uid, "SE", callback);

// ════════════════════════════════════════════════════════════════════
// grantCrests() — THE SHARED DC HELPER
// ────────────────────────────────────────────────────────────────────
// ⚠⚠ THIS IS THE MOST IMPORTANT FUNCTION IN THE EMPIRE. ⚠⚠
//
// DC is granted from MANY apps but must be granted ONE WAY. Pierre owns
// this code. Every title imports it. NOBODY reimplements it.
//
// Partners: a standalone copy of this ships as `crestGrant.js` — drop it
// in your src/, import grantCrests from it, done. Do not hand-write a
// divineCrests update. Ever.
//
// TWO THINGS IT DOES THAT YOU MUST NOT SKIP:
//   1. Delta transaction on the LEAF ONLY. Never a read-then-write on
//      the profile object — that shape caused the April 2026 holo-card
//      + crest wipe. This shape mathematically cannot do that.
//   2. Appends to pierre/mint/{uid}. DC can now be minted from a dozen
//      apps; without a log, an over-generous faucet somewhere is
//      invisible until the reserve is already inflated.
//
// LEGAL FAUCETS:   achievements · boss first-kills · secrets · the Well
// ILLEGAL FAUCETS: score conversion · daily login · anything repeatable
//
// sourceKey identifies the FAUCET, not just the app:
//   "terror:gatekeeper_first"  "perry:act1_perfect"  "pierre:achievement"
// When something inflates, you want to know WHICH faucet.
// ════════════════════════════════════════════════════════════════════

// ⟡ THE BODY MOVED TO ieEconomy.js, 2026-08-13. It is the same delta
//   transaction on the same leaf plus the same mint-log append — with
//   one addition: it refuses to grant more than GRANT_CEILING and says
//   so out loud, so a runaway faucet cannot silently outrun the Layer
//   One rules clamp and start returning permission_denied that looks
//   like a Firebase fault. Pierre keeps the export name; every existing
//   call site is untouched.
export const grantCrests = (uid, amount, sourceKey = "unknown") =>
  _grantCrests(uid, amount, sourceKey);

// DEPRECATED NAME — forwards to grantCrests so older call sites keep
// working. The old implementation did a full-object profile write and
// was illegal under Economy Law §2. Retire calls to this at leisure.
export const awardCrests = async (uid, amount, reason = "pierre:legacy") =>
  grantCrests(uid, amount, reason);

// Spend DC. Delta transaction, aborts if the player can't afford it.
// Returns true only if the debit actually committed.
export const spendCrests = (uid, amount) => _spendCrests(uid, amount);

// ════════════════════════════════════════════════════════════════════
// THE RATE TABLE  —  games/pierre/exchange/rates/{currencyId}
// ────────────────────────────────────────────────────────────────────
// Lives in Firebase so caP'n can tune without a deploy. He WILL be
// tuning these constantly.
//
// THE TOIL LAW: a rate is set so one DC costs roughly the same effort
// in EVERY title. Different numbers, same sweat.
//   Reference: one focused hour ≈ 3–5 DC.
//
// A rate is a LIVING NUMBER. The day a title buffs its payouts, its
// rate is silently wrong and stays wrong until someone finds the
// exploit. Any title that changes its earn rate files a re-check in
// the same session. Pierre is the only one positioned to notice.
// ════════════════════════════════════════════════════════════════════

export const RATES_PATH = _RATES_PATH;
export const MINT_PATH = _MINT_PATH;
export const COOLDOWN_PATH = _COOLDOWN_PATH;
export const HISTORY_PATH = _HISTORY_PATH;

// ── THE CONSOLE HANDLE ───────────────────────────────────────
// `import` does not work in a browser console on a bundled page, so the
// one query that has to be run before the Layer One clamp is pasted —
// "what is the largest Crest grant that has ever landed?" — was
// unrunnable without a code change. This is that code change.
//
// Open the Pier, sign in, and type:
//
//     await IE.largestMintEver()
//
// It reads games/pierre/mint/* and returns { max, at, sourceKey, uid }. That
// number is the floor for the clamp cap. Read-only; it cannot write.
// See gamemaster/IE_RULES_CLAMP.md §3.
if (typeof window !== "undefined") {
  window.IE = {
    ...(window.IE || {}),
    largestMintEver: _largestMintEver,
    getRates: _getRates,
    version: "recalibration-2026-08-13b",
  };
}

// ════════════════════════════════════════════════════════════════════
// ⟡ THE GREAT RECALIBRATION — 2026-08-13
// ────────────────────────────────────────────────────────────────────
// Every rate below was derived the same way, from the one unit settled
// in ieEconomy.js §1:
//
//        ONE ◆ = ONE QUARTER OF A FOCUSED HOUR OF PLAY
//        perCrest = (earned in one focused hour) / 4
//
// So a rate is no longer a taste. It is an arithmetic claim about a
// title's earn rate, and it can be checked. Where a title has never
// been measured the row says so — `provisional: true` and a note
// naming what has to be measured. A provisional rate is not a
// placeholder nobody will revisit; the Well renders it with a mark,
// which is the only reason they ever get revisited.
//
// ⚠ THE ANCHOR DID NOT MOVE. Snake Eyes stays at 1,000 — it is the one
//   currency that was actually measured (4,000 SE per focused hour,
//   2026-07-25), and re-basing the Empire off an unmeasured number to
//   make new arithmetic look tidier would have been the worst possible
//   trade. The doctrine was chosen to AGREE with the measurement, not
//   to replace it. Sol's independently-set 12 lands on 48/hour, inside
//   its own stated 40–60. Two titles tuned by feel, months apart,
//   agreeing to the same unit is the evidence that the unit is real.
// ════════════════════════════════════════════════════════════════════

export const SEED_RATES = {
  SE: {
    currencyId: "SE", path: "snakeEyesCoins", name: "Snake Eyes Coins",
    icon: "◈", owningApp: "snake-eyes", tint: "#22d3ee",
    perCrest: 1000, active: true,
    measuredAt: "2026-07-25", provisional: false,
    hourly: 4000,
    note: "THE ANCHOR. The only measured currency in the Empire. Every other rate is derived against this one.",
  },
  CC: {
    currencyId: "CC", path: "chocolateCoins", name: "Chocolate Coins",
    icon: "◕", owningApp: "terror", tint: "#c98b4b",
    perCrest: 1000, active: true,
    measuredAt: null, provisional: true,
    hourly: null,
    note: "PROVISIONAL — pegged to SE as a placeholder since July. Terror is the biggest title in the Empire and the only one whose earn rate has never been timed. Play one focused hour of Endless Depths, count the coins, divide by four.",
  },
  KD: {
    currencyId: "KD", path: "kindling", name: "Kindling",
    icon: "⋔", owningApp: "perry", tint: "#e0763a",
    perCrest: 50, active: true,
    measuredAt: null, provisional: true,
    hourly: 200,
    note: "PROVISIONAL — derived from Perry's own price list rather than a stopwatch: goods run 8–75 ⋔ and Ascension rungs 40–260, so a focused hour that buys a couple of things and a rung is ≈200 ⋔. Time a real run and re-file.",
  },
  BM: {
    currencyId: "BM", path: "annihilation___bloodMoney", name: "Lyf Force", short: "LF",
    icon: "❦", owningApp: "annihilation", tint: "#a83b3b",
    perCrest: 100, active: true,
    measuredAt: null, provisional: true,
    hourly: 400,
    note: "PROVISIONAL — and now owed a re-measure. RENAMED 2026-08-13: Blood Money / Blood Crests is retired; the currency is Lyf Force, and per caP'n's ruling it is Annihilation's ONLY currency AND its progression track — there is no separate experience number. The id stays BM (a currencyId is permanent) and the path stays annihilation___bloodMoney, because renaming the path would migrate balances players have already earned. ⚠ NOTE THE LANED PATH — a Well reading the unlaned field finds 0 and tells a solvent player they are broke. The 400/hour figure predates the Depths conversion, which moved a repeatable ◆ faucet onto this currency; the earn rate is now higher than 400 and this rate is stale until somebody times a run.",
  },
  DW: {
    currencyId: "DW", name: "Dewdrops",
    icon: "❋", owningApp: "bebecitos", tint: "#7fb98a",
    // ⟡ NOT under profiles/. The garden is its own node and no
    //   migration was needed to join the Well — one data field instead
    //   of a rule. Derived shape; see gardenState.js P.dewdrops.
    fullPath: "games/bebecitos/gardens/{uid}/dewdrops",
    perCrest: 15, active: true,
    measuredAt: null, provisional: true,
    hourly: 60,
    note: "PROVISIONAL — a Dewpebble is 3 ❋ and a new garden starts with 30. Bebecitos is deliberately slow and gentle; an hour of foraging is ≈60. Do not speed it up to make the arithmetic rounder.",
  },
  SOL: {
    currencyId: "SOL", path: "sol", name: "Sol",
    icon: "♥", owningApp: "sol-survivor", tint: "#e8b84b",
    perCrest: 12, active: true,
    measuredAt: null, provisional: true,
    hourly: 48,
    note: "Set independently in solEconomy.js from a stated 40–60 Sol/hour, months before the unit existed — and it lands on 48, dead inside the doctrine. Left exactly as it was.",
  },
  SW: {
    currencyId: "SW", path: "crumbs", name: "Crumbs", short: "CRB",
    icon: "⌁", owningApp: "swat", tint: "#c9b48b",
    perCrest: 650, active: true,
    measuredAt: null, provisional: true,
    hourly: 2600,
    note: "Set independently in swatConfig.js. Against PAYOUT (per_ruin 8–22 · house_cleared 40 · prophecy 60) an hour is ≈2,600. Consistent; left alone.",
  },
};

// ⚠ WHY THIS IS NOW PER-ROW AND NOT ONE WRITE.
// It used to seed the WHOLE TABLE in a single transaction that aborted
// if any data was already there. Which meant: the day the table existed
// with two rows in it, no new row could ever be seeded again — adding a
// title required a console visit, and the "zero deploys" promise quietly
// stopped applying to the only case anyone cared about. Per-row
// ensureCurrency() seeds what is missing and never touches what is
// there, so a rate caP'n has tuned by hand still cannot be clobbered.
export const ensureRateTable = async () => "console-owned";

// ⚠ THE RATE TABLE IS CONSOLE-OWNED AS OF 2026-08-13. ensureRateTable()
//   above is now deliberately INERT, and the seed rows survive only as
//   `SEED_RATES` — the document of record for what the table should
//   contain, and the source the console paste is generated from.
//
//   WHY: `games/pierre/exchange/rates` is published `".write": false`.
//   A client-writable rate table is a client that can print money, and
//   the Layer One delta clamp cannot see it happening. Set perCrest to
//   0.0001, exchange one Crumb, mint Crests forever — every individual
//   write looks legal, so the clamp never fires. Clamping the currencies
//   while leaving the RATE client-writable buys nothing.
//
//   And append-only rates would not have closed it either: `profiles/$uid`
//   has no `$extra` guard, so a player can create an arbitrary field on
//   their own profile, then seed a NEW row pointing at it with a
//   perCrest of 1. The write policy has to be false, not "create-only".
//
//   TO ADD OR RETUNE A CURRENCY: edit SEED_RATES here (so the repo stays
//   the document of record), then paste the row into the Firebase console
//   at games/pierre/exchange/rates/{ID}. One paste. Still zero deploys —
//   the Well reads live and every title's wallet updates immediately.
//   See gamemaster/PIERRE_RATES_SEED.json.

// Live rate table — ACTIVE rows only. Inactive currencies never reach a
// picker. Normalisation lives in ieEconomy.js so every title's wallet
// and the Well agree on what a row means.
// ════════════════════════════════════════════════════════════════════
// 🧪 THE TESTING WELL  (caP'n, 2026-09-16)
// ────────────────────────────────────────────────────────────────────
// "we are testing and it's only me playing … everything 1-1 except DC.
//  1 DC = 100 currency of any other game, however you can exchange 1 of
//  any currency for 100 DC." — deliberately bonkers, so testing never
//  waits on a grind.
//
//   • DC  → X  :  1 ◆  = 100 X
//   • X   → DC :  1 X  = 100 ◆
//   • X   → Y  :  1 X  = 1 Y      (one direct leg, no ◆ midpoint)
//   • no 10-minute cooldown while testing
//   • ✹ Dawgs stay ACQUIRE-ONLY — you can buy them, never sell them.
//
// The console rate rows are NOT touched; they are what comes back the
// moment WELL_TESTING flips to false. ONE const. That is the whole exit.
// ════════════════════════════════════════════════════════════════════
export const WELL_TESTING  = true;
export const TEST_DC_OUT   = 100;   // 1 ◆ buys this many of anything
export const TEST_DC_IN    = 100;   // 1 of anything buys this many ◆
const TEST_BITE            = 2500;  // under the 5,000 rules clamp

// ⚠ WHY ✹ DAWGS NEVER SHOWED UP AT THE WELL (found 2026-09-16).
// `ensureDawgsRegistered()` writes its row to games/pierre/exchange/rates,
// which is published `".write": false` — so the seed has failed silently
// on every boot and UD was never in the picker. caP'n bought "15" of
// what he thought was Dawgs; ❋ Dewdrops (DW, 15 to the ◆) sat where
// Dawgs should have been. Pierre owns UD, so Pierre can supply its own
// row in code. A console row, if one is ever pasted, still wins.
const withPierRows = (rates) => {
  if (!rates) return rates;
  return rates[UD_ID] ? rates : { ...rates, [UD_ID]: { ...UD_ROW } };
};

export const listenToRates = (callback) => _listenToRates((r) => callback(withPierRows(r)));

export const getRates = async () => withPierRows(await _getRates());

// Add `amount` to a leaf in bites under the clamp. Returns what landed.
const creditChunked = async (path, amount, bite = TEST_BITE) => {
  let landed = 0;
  while (landed < amount) {
    const step = Math.min(bite, amount - landed);
    // eslint-disable-next-line no-await-in-loop
    const tx = await runTransaction(ref(db, path), (bal) => (bal || 0) + step);
    if (!tx.committed) break;
    landed += step;
  }
  return landed;
};

// ─── COOLDOWN ────────────────────────────────────────────────
// 10 minutes, per player. Unchanged. This is the ONLY brake on the
// Well — it is what stops cross-currency exchange from becoming a
// free arbitrage machine. Do not shorten it casually.

export const EXCHANGE_COOLDOWN_MS = 10 * 60 * 1000;

export const getExchangeCooldown = async (uid) => {
  const snap = await get(ref(db, `${_COOLDOWN_PATH}/${uid}`));
  if (!snap.exists()) return 0;
  const elapsed = Date.now() - snap.val();
  return elapsed >= EXCHANGE_COOLDOWN_MS ? 0 : EXCHANGE_COOLDOWN_MS - elapsed;
};

export const listenToExchangeCooldown = (uid, callback) => {
  return onValue(ref(db, `${_COOLDOWN_PATH}/${uid}`), snap => {
    if (!snap.exists()) return callback(0);
    const elapsed = Date.now() - snap.val();
    callback(elapsed >= EXCHANGE_COOLDOWN_MS ? 0 : EXCHANGE_COOLDOWN_MS - elapsed);
  });
};

// ════════════════════════════════════════════════════════════════════
// THE EXCHANGE  —  N-WAY, ROUTED THROUGH DC
// ────────────────────────────────────────────────────────────────────
// Every currency exchanges into DC. DC exchanges out into any currency.
// Cross-currency (CC → SE) routes as TWO LEGS through DC, and both legs
// are shown. We never hide the routing.
//
// NO FEE. Not on either leg. Not on the round trip.
//
// ── ROUNDING, AND WHY IT WORKS THE WAY IT DOES ───────────────
// Into DC we floor — dust never mints a Crest. But we only ever CHARGE
// the amount actually consumed, so the remainder stays in the player's
// pocket instead of being silently burned. Offering 1,500 SE at a
// 1,000 rate spends 1,000 and returns 1 DC; the other 500 never leaves.
// Silent burn is exactly the kind of quiet theft that erodes trust in
// a bank, and this Well is meant to be trusted.
// ════════════════════════════════════════════════════════════════════

export const DC_ID = "DC";

// Pure planner. No reads, no writes — feed it the live rate table.
// Returns:
//   { valid, legs:[{fromId,toId,input,output,perCrest}], spend, output,
//     crests, remainder, reason }
//   spend     — what actually leaves the player's balance
//   remainder — what they offered but we didn't need (never charged)
//   crests    — DC at the midpoint (equals output when toId === "DC")
export const planExchange = (fromId, toId, rawAmount, rates) => {
  const amount = Math.floor(Number(rawAmount) || 0);
  if (amount <= 0) return { valid: false, reason: "Enter an amount." };
  if (!fromId || !toId) return { valid: false, reason: "Pick both sides." };
  if (fromId === toId) return { valid: false, reason: "Pick two different currencies." };

  // ── ⭐ GATE 1 OF 3 · THE ACQUIRE-ONLY LAW ─────────────────────────
  // caP'n: "you can never exchange UP Dawgs for anything, you can only
  // exchange to obtain them." An acquire-only currency is legal as a
  // TARGET and refused as a SOURCE, always.
  //
  // ⚠ WHY THIS GATE EXISTS AT ALL, in one line: the Minerium pays Dawgs
  // on a TIMER, and a timer with a route to ◆ is an AFK machine that
  // mints the Empire's reserve overnight. THE FAUCET LAW forbids that
  // shape; this refusal is what keeps a passive faucet inside it.
  // The other two gates are in executeExchange (below) and in the Well
  // UI. Removing any ONE of them must not open the door.
  if (isAcquireOnly(fromId)) return { valid: false, reason: ACQUIRE_ONLY_REASON };

  const fromRate = fromId === DC_ID ? null : rates?.[fromId];
  const toRate   = toId   === DC_ID ? null : rates?.[toId];

  if (fromId !== DC_ID && !fromRate) return { valid: false, reason: `${fromId} isn't registered at the Well yet.` };
  if (toId   !== DC_ID && !toRate)   return { valid: false, reason: `${toId} isn't registered at the Well yet.` };

  if (WELL_TESTING) {
    let output, crests;
    if (toId === DC_ID)        { output = amount * TEST_DC_IN;  crests = output; }
    else if (fromId === DC_ID) { output = amount * TEST_DC_OUT; crests = amount; }
    else                       { output = amount;               crests = 0; }
    return {
      valid: true, testing: true, fromId, toId,
      spend: amount, output, crests, remainder: 0,
      legs: [{ fromId, toId, input: amount, output, perCrest: null }],
    };
  }

  const legs = [];
  let crests, spend, output, remainder = 0;

  if (toId === DC_ID) {
    // ── currency → DC. Floor; charge only what's consumed.
    crests = Math.floor(amount / fromRate.perCrest);
    if (crests < 1) {
      return {
        valid: false,
        reason: `Need at least ${fromRate.perCrest.toLocaleString()} ${fromId} for 1 ◆.`,
      };
    }
    spend     = crests * fromRate.perCrest;
    remainder = amount - spend;
    output    = crests;
    legs.push({ fromId, toId: DC_ID, input: spend, output: crests, perCrest: fromRate.perCrest });

  } else if (fromId === DC_ID) {
    // ── DC → currency. Exact, no rounding loss.
    crests = amount;
    spend  = amount;
    output = amount * toRate.perCrest;
    legs.push({ fromId: DC_ID, toId, input: amount, output, perCrest: toRate.perCrest });

  } else {
    // ── cross-currency. Two legs through DC. Both shown.
    crests = Math.floor(amount / fromRate.perCrest);
    if (crests < 1) {
      return {
        valid: false,
        reason: `Need at least ${fromRate.perCrest.toLocaleString()} ${fromId} to reach 1 ◆.`,
      };
    }
    spend     = crests * fromRate.perCrest;
    remainder = amount - spend;
    output    = crests * toRate.perCrest;
    legs.push({ fromId, toId: DC_ID, input: spend,  output: crests, perCrest: fromRate.perCrest });
    legs.push({ fromId: DC_ID, toId, input: crests, output,         perCrest: toRate.perCrest });
  }

  return { valid: true, legs, spend, output, crests, remainder, fromId, toId };
};

// The minimum offering that yields anything at all, for placeholder text.
export const minimumFor = (fromId, rates) =>
  (WELL_TESTING || fromId === DC_ID) ? 1 : (rates?.[fromId]?.perCrest || 1);

// What one unit of `fromId` buys of `toId` right now — for the counter's
// rate line, so the UI never does its own arithmetic.
export const rateLine = (fromId, toId, rates) => {
  const p = planExchange(fromId, toId, 1, rates);
  return p.valid ? p.output : null;
};

// ─── EXECUTE ─────────────────────────────────────────────────
// Ladder, in order. Money never moves backwards past step 3.
//   1. Cooldown gate.
//   2. Re-read rates FRESH from Firebase. Never trust the rate the
//      client was holding — caP'n may have retuned it mid-session,
//      and a stale client rate is a free money bug.
//   3. Debit the source, delta transaction, aborts if short.
//   4. Credit the target. DC output goes through grantCrests() so it
//      lands in the mint log like every other Crest ever minted.
//      If the credit fails, we REFUND the debit immediately.
//   5. Stamp the cooldown.
//   6. Append history — cut: 0, forever.
// Carries how much actually landed out of the credit loop, so the catch
// can refund the difference instead of the whole offering.
class PartialCredit extends Error {
  constructor(credited) { super("partial_credit"); this.credited = credited; }
}

export const executeExchange = async (uid, fromId, toId, rawAmount) => {
  if (!uid) return { success: false, reason: "Not signed in." };

  // ── ⭐ GATE 2 OF 3 · THE ACQUIRE-ONLY LAW, re-checked ─────────────
  // planExchange already refused this, and that is exactly why it is
  // checked again here. A planner is advice; this function moves money.
  // The day somebody calls executeExchange from a new surface without
  // planning first, this line is the one that holds. It costs nothing
  // and it sits ABOVE the cooldown read so a refusal never burns one.
  if (isAcquireOnly(fromId)) return { success: false, reason: ACQUIRE_ONLY_REASON };

  const cooldown = WELL_TESTING ? 0 : await getExchangeCooldown(uid);
  if (cooldown > 0) {
    const mins = Math.ceil(cooldown / 60000);
    return { success: false, reason: `The Well is still settling. ${mins} min.` };
  }

  const rates = await getRates();
  const plan  = planExchange(fromId, toId, rawAmount, rates);
  if (!plan.valid) return { success: false, reason: plan.reason };

  // ⟡ RESOLVED FROM THE LIVE ROWS, NOT FROM A CONST. This is the line
  //   that stranded three currencies: it used to read the path out of a
  //   hardcoded three-entry map, so a currency with a perfectly good
  //   rate row still failed here with "Unknown currency." balancePath()
  //   also honours `fullPath`, which is how Dewdrops trade out of the
  //   garden without ever having been moved into profiles/.
  const fromMeta = _currencyMeta(fromId, rates);
  const toMeta   = _currencyMeta(toId, rates);
  const fromPath = fromId === _DC_ID ? `profiles/${uid}/divineCrests` : balancePath(uid, fromMeta);
  const toPath   = toId   === _DC_ID ? `profiles/${uid}/divineCrests` : balancePath(uid, toMeta);
  if (!fromPath || !toPath) {
    return {
      success: false,
      reason: `No known balance path for ${!fromPath ? fromId : toId}. Its rate row needs a \`path\` or a \`fullPath\`. Tell caP'n.`,
    };
  }

  // 3) Debit the source.
  const debit = await runTransaction(
    ref(db, fromPath),
    (bal) => {
      const b = bal || 0;
      if (b < plan.spend) return undefined; // abort — insufficient funds
      return b - plan.spend;
    }
  );
  if (!debit.committed) {
    return { success: false, reason: `Not enough ${fromMeta.name}.` };
  }

  // 4) Credit the target.
  // ⚠ `credited` LIVES OUTSIDE THE TRY, and that is the whole point. The
  // first version of this fix declared it inside, so the catch could only
  // learn what had landed from `PartialCredit` — which is thrown on the
  // ONE failure mode where a transaction resolves uncommitted. The
  // realistic failures (permission_denied, the Layer-One clamp, a dropped
  // socket) REJECT instead, `landed` fell back to 0, and the catch issued
  // a full refund with nine of ten bites still in the player's balance.
  // The faucet the fix was written to close, reopened by a `let`.
  let credited = 0;
  try {
    if (toId === DC_ID) {
      // The Well is a legal DC faucet — log it like any other mint.
      // ⚠ grantCrests returns what LANDED. A short mint is the same
      // partial as a short credit and takes the same road out.
      const minted = await grantCrests(uid, plan.output, `pierre:well:${fromId}`);
      credited = Number(minted) || 0;
      if (credited < plan.output) throw new PartialCredit(credited);
    } else {
      // ⚠ CHUNKED, for the same reason grantCrests() is: the Layer One
      //   clamp refuses any single write that jumps more than 200
      //   Crests' worth of a currency, and a player converting a big
      //   pile of ◆ out is entitled to all of it. One oversized write
      //   would be refused with a bare permission_denied AFTER the
      //   debit had already committed — the player pays and receives
      //   nothing, and the refund path below is the only thing between
      //   them and a robbed account. Several small writes never reach
      //   the ceiling. See gamemaster/IE_RULES_CLAMP.md §5.
      const bite = WELL_TESTING ? TEST_BITE : Math.max(1, (rates?.[toId]?.perCrest || 1) * 200);
      while (credited < plan.output) {
        const step = Math.min(bite, plan.output - credited);
        // ⚠⚠ `runTransaction` DOES NOT THROW WHEN IT FAILS TO COMMIT —
        // it resolves with `{ committed: false }`. This loop used to do
        // `credited += step` unconditionally, so a refused write left the
        // player debited, the balance untouched, and the Well cheerfully
        // returning `success: true` with the full output printed on the
        // receipt. Proven: 1 ◆ taken, 0 ✹ delivered, "success".
        // The debit twenty lines up has always checked `committed`; the
        // credit not checking it was the asymmetry that hid this.
        // eslint-disable-next-line no-await-in-loop
        const step_tx = await runTransaction(ref(db, toPath), (bal) => (bal || 0) + step);
        if (!step_tx.committed) throw new PartialCredit(credited);
        credited += step;
      }
    }
  } catch (err) {
    // ── THE REFUND, AND IT IS ONLY EVER A **FULL** REFUND WHEN NOTHING
    //    LANDED. ⚠⚠ It used to return `plan.spend` whatever had already
    //    been credited, so a ladder that failed on its second bite left
    //    the player holding the first bite AND their whole offering
    //    back — free money, at 200 Crests' worth a step.
    // ⛑ Same lesson as the harvest and the quest board: a rollback that
    //    does not look at what was already paid is a faucet.
    const landed = err instanceof PartialCredit ? err.credited : credited;
    if (landed <= 0) {
      // Chunked: a big ◆ offering handed back in one write would trip the
      // clamp and the refund itself would be refused.
      await creditChunked(fromPath, plan.spend);
      return { success: false, reason: "The Well faltered — your offering was returned." };
    }
    if (plan.testing) {
      // Undelivered output converted back to input, rounded DOWN.
      const ratio = plan.output / plan.spend;          // 100, 1, or 100
      const unspentT = Math.floor((plan.output - landed) / ratio);
      if (unspentT > 0) await creditChunked(fromPath, unspentT);
      return {
        success: false, partial: true, credited: landed, refunded: unspentT,
        reason: `The Well faltered part-way. ${landed.toLocaleString()} ${toMeta?.name || toId} arrived and the rest of your offering came back.`,
      };
    }
    // Part of it arrived. Refund only the share that did not, rounded
    // DOWN in the house's favour so a rounding error can never mint.
    // ⚠ EXPLICIT. Divine Crests are not a rate row (there is no `DC`
    // entry, by design), so this used to fall through to `|| 1` and
    // happened to be right. The day somebody registers a DC row with a
    // perCrest that is not 1, a silent rescale of every refund is not a
    // failure anyone would attribute to this line.
    const perCrest = toId === _DC_ID ? 1 : (Number(rates?.[toId]?.perCrest) || 1);
    const unspent = Math.floor((plan.output - landed) / perCrest) * (Number(fromMeta?.perCrest) || 1);
    if (unspent > 0) await runTransaction(ref(db, fromPath), (bal) => (bal || 0) + unspent);
    return {
      success: false, partial: true, credited: landed, refunded: unspent,
      reason: `The Well faltered part-way. ${landed.toLocaleString()} ${toMeta?.name || toId} arrived and the rest of your offering came back.`,
    };
  }

  const now = Date.now();

  // 5) Cooldown. (Skipped while testing — otherwise the Well's own
  //    listener would lock the button for ten minutes anyway.)
  if (!WELL_TESTING) await set(ref(db, `${_COOLDOWN_PATH}/${uid}`), now);

  // 6) History. `cut` stays in the schema and is 0 forever — we never
  //    rewrite a player's old rows, and we never charge a new one.
  await set(ref(db, `${_HISTORY_PATH}/${uid}/${now}`), {
    type: `${fromId.toLowerCase()}_to_${toId.toLowerCase()}`,
    fromId, toId,
    input:  plan.spend,
    output: plan.output,
    crests: plan.crests,
    legs:   plan.legs.length,
    cut:    0,
    testing: WELL_TESTING ? 1 : 0,
    ts:     now,
  });

  return {
    success: true,
    spend: plan.spend,
    output: plan.output,
    crests: plan.crests,
    remainder: plan.remainder,
    fromId, toId,
  };
};

// ─── HISTORY ─────────────────────────────────────────────────
export const getExchangeHistory = async (uid) => {
  const snap = await get(ref(db, `${_HISTORY_PATH}/${uid}`));
  if (!snap.exists()) return [];
  return Object.entries(snap.val())
    .map(([ts, data]) => ({ ...data, timestamp: Number(ts) }))
    .sort((a, b) => b.timestamp - a.timestamp)
    .slice(0, 20);
};

// ─── MINT LOG (audit) ────────────────────────────────────────
// Every Crest this player has ever been granted, newest first. Pierre's
// audit trail against a runaway faucet in some other title.
export const getMintLog = async (uid) => {
  const snap = await get(ref(db, `${_MINT_PATH}/${uid}`));
  if (!snap.exists()) return [];
  return Object.entries(snap.val())
    .map(([key, v]) => ({ ...v, key }))
    .sort((a, b) => (b.at || 0) - (a.at || 0))
    .slice(0, 50);
};


// ════════════════════════════════════════════════════════════════════
// IE COSMETICS — SHARED IDENTITY SLICE
// ════════════════════════════════════════════════════════════════════
//
// ⚠ EQUIP-ANYWHERE LANE RULE ⚠
//
// Per IE Cosmetic Schema v1.1 (Apr 2026): cosmetics are the INVERSE of
// currency. Any IE game can grant unlocks. Any IE game can equip. All
// IE games render the equipped state immediately via listenToEquipped.
//
// The Pier is the DISCOVERY/ORGANIZATION hub (the Wardrobe) — it is
// NOT an equipping chokepoint. You equip wherever you earned it.
//
// This block is safe to paste VERBATIM into:
//   - Terror's   progression.js
//   - Pierre's   progression.js
//   - Snake Eyes' progression.js
//
// Shape MUST stay identical across all three copies. If you change
// this file, update IE_COSMETIC_SCHEMA_v1.md first, bump the version,
// then propagate to all three apps in the same session.
//
// Canonical reference: IE_COSMETIC_SCHEMA_v1.md
// ════════════════════════════════════════════════════════════════════

// ── COSMETIC SLOTS ────────────────────────────────────────────────────
// Six fixed slots, one item per slot. Locked by schema v1.1.
// If you need a "particle trail + glow halo + aura" combo, those are
// sub-effects of the `aura` slot — do NOT add new top-level slots.

export const COSMETIC_SLOTS = ["head", "face", "body", "back", "aura", "title"];

// ── RARITY TIERS ──────────────────────────────────────────────────────
// Six tiers. Rarity is metadata — each game interprets it with its own
// aesthetic (Terror: manuscript-ink legendary; Casino: neon holo; etc.)

export const COSMETIC_RARITIES = [
  "common", "uncommon", "rare", "epic", "legendary", "mythic",
];

// Display metadata — shared so "Epic" and "Mythic" feel consistent
// across every IE app even if their visual treatment differs.
export const RARITY_META = {
  common:    { order: 0, label: "Common",    short: "C",  tint: "#9aa4ad" },
  uncommon:  { order: 1, label: "Uncommon",  short: "U",  tint: "#4da36a" },
  rare:      { order: 2, label: "Rare",      short: "R",  tint: "#4a88c8" },
  epic:      { order: 3, label: "Epic",      short: "E",  tint: "#8a5cd6" },
  legendary: { order: 4, label: "Legendary", short: "L",  tint: "#d4a84b" },
  mythic:    { order: 5, label: "Mythic",    short: "M",  tint: "#c94a6b" },
};

// ── VALIDATION HELPERS (internal) ─────────────────────────────────────

const _validSlot   = (slot)   => COSMETIC_SLOTS.includes(slot);
const _validRarity = (rarity) => COSMETIC_RARITIES.includes(rarity);

// ── GRANT ─────────────────────────────────────────────────────────────
// Award a cosmetic unlock. Append-only: if already unlocked, this is a
// no-op — original unlockedAt and source are preserved.
//
// meta: { source: "terror:...", rarity: "legendary", slot: "head" }
//   - source: namespaced string identifying where/why it was earned
//   - rarity: one of COSMETIC_RARITIES
//   - slot:   one of COSMETIC_SLOTS (the slot this cosmetic occupies)
//
// Returns: { success, alreadyUnlocked, reason? }

export const grantCosmetic = async (uid, cosmeticId, meta = {}) => {
  if (!uid || !cosmeticId) {
    return { success: false, reason: "Missing uid or cosmeticId." };
  }
  const { source, rarity, slot } = meta;
  if (!_validSlot(slot)) {
    return { success: false, reason: `Invalid slot: ${slot}` };
  }
  if (!_validRarity(rarity)) {
    return { success: false, reason: `Invalid rarity: ${rarity}` };
  }
  if (!source || typeof source !== "string") {
    return { success: false, reason: "Missing or invalid source." };
  }

  const cosmeticRef = ref(db, `profiles/${uid}/unlockedCosmetics/${cosmeticId}`);
  const snap = await get(cosmeticRef);

  if (snap.exists()) {
    // Append-only: never overwrite. Provenance is permanent.
    return { success: true, alreadyUnlocked: true };
  }

  await set(cosmeticRef, {
    unlockedAt: Date.now(),
    source,
    rarity,
    slot,
  });

  return { success: true, alreadyUnlocked: false };
};

// ── EQUIP / UNEQUIP ───────────────────────────────────────────────────
// Equip a cosmetic to its slot. Verifies the player owns it first, and
// that the stored slot matches the target slot (prevents ID collisions
// from cross-app catalogs silently equipping to the wrong slot).
//
// Returns: { success, reason? }

export const equipCosmetic = async (uid, slot, cosmeticId) => {
  if (!uid || !slot || !cosmeticId) {
    return { success: false, reason: "Missing uid, slot, or cosmeticId." };
  }
  if (!_validSlot(slot)) {
    return { success: false, reason: `Invalid slot: ${slot}` };
  }

  const unlockSnap = await get(ref(db, `profiles/${uid}/unlockedCosmetics/${cosmeticId}`));
  if (!unlockSnap.exists()) {
    return { success: false, reason: "Not unlocked." };
  }

  const unlock = unlockSnap.val();
  if (unlock.slot !== slot) {
    return {
      success: false,
      reason: `Slot mismatch: ${cosmeticId} belongs in "${unlock.slot}", not "${slot}".`,
    };
  }

  await update(ref(db, `profiles/${uid}/equipped`), { [slot]: cosmeticId });
  return { success: true };
};

// Unequip a slot (equip null).
export const unequipSlot = async (uid, slot) => {
  if (!uid || !_validSlot(slot)) {
    return { success: false, reason: `Invalid slot: ${slot}` };
  }
  await update(ref(db, `profiles/${uid}/equipped`), { [slot]: null });
  return { success: true };
};

// Unequip every slot at once (useful for "reset to default" UI).
export const unequipAll = async (uid) => {
  if (!uid) return { success: false };
  const cleared = {};
  for (const s of COSMETIC_SLOTS) cleared[s] = null;
  await update(ref(db, `profiles/${uid}/equipped`), cleared);
  return { success: true };
};

// ── READ HELPERS ──────────────────────────────────────────────────────

// One-shot read of all equipped slots. Returns a plain object with all
// six slots populated (values are cosmeticId strings or null).
export const getEquipped = async (uid) => {
  const empty = Object.fromEntries(COSMETIC_SLOTS.map((s) => [s, null]));
  if (!uid) return empty;
  const snap = await get(ref(db, `profiles/${uid}/equipped`));
  const val = snap.val() || {};
  return { ...empty, ...val };
};

// Live listener for equipped state. Callback fires with the normalized
// { head, face, body, back, aura, title } object on every change.
// Returns the unsubscribe function.
export const listenToEquipped = (uid, callback) => {
  const empty = Object.fromEntries(COSMETIC_SLOTS.map((s) => [s, null]));
  return onValue(ref(db, `profiles/${uid}/equipped`), (snap) => {
    callback({ ...empty, ...(snap.val() || {}) });
  });
};

// One-shot read of the full unlock map.
// Shape: { [cosmeticId]: { unlockedAt, source, rarity, slot } }
export const getUnlocked = async (uid) => {
  if (!uid) return {};
  const snap = await get(ref(db, `profiles/${uid}/unlockedCosmetics`));
  return snap.val() || {};
};

// Live listener for the full unlock map.
export const listenToUnlocked = (uid, callback) => {
  return onValue(ref(db, `profiles/${uid}/unlockedCosmetics`), (snap) => {
    callback(snap.val() || {});
  });
};

// Quick check: does this uid own this cosmetic?
export const isUnlocked = async (uid, cosmeticId) => {
  if (!uid || !cosmeticId) return false;
  const snap = await get(ref(db, `profiles/${uid}/unlockedCosmetics/${cosmeticId}`));
  return snap.exists();
};

// ── SOURCE/PROVENANCE QUERIES ─────────────────────────────────────────
// These operate on an already-fetched unlock map so callers don't pay
// for redundant Firebase reads when they already have the data.

// Filter unlocks to those earned in a given app namespace.
// appPrefix: "terror" | "casino" | "pier" | "empire" | ...
export const filterUnlocksByApp = (unlockMap, appPrefix) => {
  const out = {};
  const prefix = `${appPrefix}:`;
  for (const [id, entry] of Object.entries(unlockMap || {})) {
    if (typeof entry?.source === "string" && entry.source.startsWith(prefix)) {
      out[id] = entry;
    }
  }
  return out;
};

// Filter unlocks to those matching a slot.
export const filterUnlocksBySlot = (unlockMap, slot) => {
  const out = {};
  for (const [id, entry] of Object.entries(unlockMap || {})) {
    if (entry?.slot === slot) out[id] = entry;
  }
  return out;
};

// Filter unlocks to those matching a rarity.
export const filterUnlocksByRarity = (unlockMap, rarity) => {
  const out = {};
  for (const [id, entry] of Object.entries(unlockMap || {})) {
    if (entry?.rarity === rarity) out[id] = entry;
  }
  return out;
};

// Sort unlocks most-recent-first. Returns an array of [id, entry] tuples.
export const sortUnlocksByRecency = (unlockMap) => {
  return Object.entries(unlockMap || {})
    .sort((a, b) => (b[1]?.unlockedAt || 0) - (a[1]?.unlockedAt || 0));
};

// Sort unlocks by rarity (rarest first). Returns [id, entry] tuples.
export const sortUnlocksByRarity = (unlockMap) => {
  return Object.entries(unlockMap || {}).sort((a, b) => {
    const ra = RARITY_META[a[1]?.rarity]?.order ?? -1;
    const rb = RARITY_META[b[1]?.rarity]?.order ?? -1;
    return rb - ra;
  });
};

// ════════════════════════════════════════════════════════════════════
// END OF IE COSMETICS — SHARED IDENTITY SLICE
// ════════════════════════════════════════════════════════════════════
